import React, { useState } from "react";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import { Box, Button, DialogContentText, Grid, Tooltip } from "@mui/material";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useQuery } from "urql";
import { PreviewContentDialog } from "../../../Dashboard/Tasks/PreviewContentDialog";
import {
  AlertDialog,
  DataGrid,
  FormGrid,
  SimpleTable,
} from "../../../../components";

import { metadataCardTabs } from "../constants";

const RelationshipsTable = ({
  defaultValues = {
    name: "",
    type: "",
    source: "",
    program: "",
  },
  objectName,
  objectType,
  programOptions,
  readonly,
}) => {
  const objectTypeHeader = objectType[0].toUpperCase() + objectType.slice(1);

  const [openDialog, setOpenDialog] = useState({
    isOpen: false,
    dialogName: "",
    dialogTitle: "",
  });
  const [alertDialogProps, setAlertDialogProps] = useState({
    open: false,
  });
  const [selectedProgramData, setSelectedProgramData] = useState([]);

  const { control: formContextControl, getValues: getValuesId } =
    useFormContext();
  const dialogFormMethods = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: defaultValues,
  });
  const { control, formState, getValues, reset, watch } = dialogFormMethods;
  const { isValid } = formState;
  const {
    fields: relationships,
    prepend,
    remove,
  } = useFieldArray({
    name: `${objectType}.relationships`,
    control: formContextControl,
  });
  const { name: nameField, source, program } = watch();

  const programId = programOptions.find(
    (availableProgram) => program === availableProgram?.value,
  )?.id;

  const [result, getProgramData] = useQuery({
    query:
      objectType === "passage"
        ? searchPassagesByProgramQuery
        : searchItemsByProgramQuery,
    variables: {
      program: programId,
      name: nameField.trim(),
      rowLimit: 100,
    },
    requestPolicy: "cache-and-network",
    pause: true,
  });
  const { data: programData, fetching, error, operation } = result;

  const programQueryId = operation?.variables?.program;

  const [previewContentDialogProps, setPreviewContentDialogProps] = useState({
    open: false,
  });

  const openPreviewContentDialog = (object) => (_e) => {
    setPreviewContentDialogProps({
      onClose: () => setPreviewContentDialogProps({ open: false }),
      open: true,
      object,
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    getProgramData();
  };

  const closeDialogResetForm = () => {
    setOpenDialog({ isOpen: false });
    reset({ ...defaultValues });
    setSelectedProgramData([]);
  };

  const relationshipIsUnique = ({ name, source, type }) => {
    const unavailableOptions = relationships
      .filter(
        (relationship) =>
          relationship?.name === name.trim() &&
          relationship?.source === source &&
          relationship?.type === type,
      )
      .map((option) => option.name);
    if (unavailableOptions.includes(name.trim())) {
      setAlertDialogProps({
        message: (
          <DialogContentText>{`A relationship already exists between this ${objectType} and ${name}`}</DialogContentText>
        ),
        onClose: () => setAlertDialogProps({ open: false }),
        open: true,
        dialogTitle: `Error Creating the Relationship`,
      });
      return false;
    } else {
      return true;
    }
  };

  const saveNewRelationship = (data) => {
    const vals = getValues();
    const { program, name, ...restVals } = vals;
    if (!data.length) {
      if (!relationshipIsUnique(vals)) return;
      prepend({
        ...vals,
        name: name.trim(),
      });
    } else if (data.length) {
      // add checked data to fieldArray
      data.forEach((datum) =>
        prepend({ ...restVals, id: datum?.id, name: datum?.name, program }),
      );
    }
    closeDialogResetForm();
  };

  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setSelectedProgramData(selectedRows);
  };

  const relationshipControls = metadataCardTabs(false)[objectType].find(
    (tab) => tab.label === "Relationships",
  ).controls;

  const relationshipForm = (
    <form>
      {relationshipControls.map((obj) => {
        if (source !== "CDE" && obj?.name === "program") return null; // only render program field when source is CDE
        const formGridProps = {
          ...obj,
          control,
          id: obj?.name,
          options: obj?.name === "program" ? programOptions : obj?.options,
          rules:
            source === "CDE" && obj?.name === "name"
              ? { required: false, maxLength: null }
              : obj?.rules,
        };
        return (
          <React.Fragment key={obj.name}>
            <FormGrid {...formGridProps} />
            {source === "CDE" && obj?.name === "name" && (
              <Button
                aria-label="Search"
                disabled={!program}
                onClick={handleSearch}
                sx={{ marginBottom: 1 }}
                type="submit"
              >
                Search
              </Button>
            )}
          </React.Fragment>
        );
      })}
    </form>
  );

  const relationshipsDialogProps = {
    open: true,
    fullWidth: true,
    maxWidth: source === "CDE" ? "md" : "sm",
    dialogTitle: openDialog?.dialogTitle,
    dialogBody: relationshipForm,
    customFooter: (
      <>
        <Button
          onClick={() => closeDialogResetForm()}
          aria-label="Cancel"
          variant="text"
        >
          Cancel
        </Button>
        <Button
          type="text"
          onClick={() => saveNewRelationship(selectedProgramData)}
          disabled={
            !isValid || (source === "CDE" && !selectedProgramData.length)
          }
          aria-label="Add"
        >
          Add
        </Button>
      </>
    ),
  };

  const filteredProgramData = () => {
    const selectedProgramId = programOptions.find(
      (availableProgram) => program === availableProgram?.value,
    )?.id;
    return programData &&
      programData[`${objectType}sByMetadata`] &&
      // only show cached graph results when selected program id and query id are the same
      selectedProgramId === programQueryId
      ? programData[`${objectType}sByMetadata`].filter(
          (d) => d.name !== objectName,
        )
      : [];
  };

  const rows = filteredProgramData();
  const dataGridProps = {
    checkboxSelection: true,
    columns: [
      {
        field: "name",
        flex: 1,
        headerName: `${objectTypeHeader} Name`,
        renderCell: (params) => {
          const disabledOption = relationships.filter(
            (relationship) =>
              relationship?.name === params.row?.name &&
              relationship?.source === "CDE",
          );
          if (disabledOption.length) {
            return (
              <Tooltip
                title={`Relationship Type: ${disabledOption[0]["type"]}`}
              >
                <span>{params.row?.name}</span>
              </Tooltip>
            );
          } else {
            return <span>{params.row?.name}</span>;
          }
        },
      },
    ],
    error: error,
    isRowSelectable: ({ row: { name } }) =>
      !relationships.map((d) => d.name).includes(name),
    loading: fetching,
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
    sortModel: [
      {
        field: "name",
        sort: "asc",
      },
    ],
  };

  const tableProps = {
    actions: [
      {
        disabled: readonly,
        label: "Delete",
        onClick: (row) => (e) => {
          setAlertDialogProps({
            confirmButtonText: "Delete",
            message: (
              <DialogContentText aria-label="Delete Relationship Alert Text">
                {`Delete Relationship ${row?.vals?.name}?`}
              </DialogContentText>
            ),
            onClose: (confirmed) => {
              setAlertDialogProps({ open: false });
              if (!confirmed) return;
              remove(row?.idx);
            },
            open: true,
            dialogTitle: `Delete Relationship`,
          });
        },
      },
      {
        label: "Preview",
        onClick: (row) => {
          const id = getValuesId(`${objectType}.relationships[${row.idx}].id`);
          return openPreviewContentDialog({
            id,
            type: objectTypeHeader,
          });
        },
      },
    ],
    columns: [
      { title: `${objectTypeHeader} Name`, identifier: "name" },
      { title: "Relationship Type", identifier: "type" },
      { title: "Data Source", identifier: "source" },
      { title: "Program", identifier: "program" },
    ],
    error: false,
    rows: relationships,
  };

  return (
    <>
      {!readonly && (
        <Box sx={{ display: "flex", justifyContent: "end", marginBottom: 1 }}>
          <Button
            onClick={() =>
              setOpenDialog({
                isOpen: true,
                dialogName: "createRelationship",
                dialogTitle: `Add ${objectTypeHeader} Relationship`,
              })
            }
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Add Relationship
          </Button>
        </Box>
      )}
      {openDialog.isOpen && (
        <FormProvider {...dialogFormMethods}>
          <AlertDialog {...relationshipsDialogProps}>
            <Grid container spacing={2} justifyContent="space-between">
              <Grid
                item
                xs={
                  openDialog.dialogName === "deleteRelationship"
                    ? 12
                    : source === "CDE"
                      ? 4
                      : 8
                }
              >
                {relationshipsDialogProps.dialogBody}
              </Grid>
              {source === "CDE" && (
                <Grid item xs={7}>
                  <DataGrid {...dataGridProps}></DataGrid>
                </Grid>
              )}
            </Grid>
          </AlertDialog>
        </FormProvider>
      )}
      {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
      <SimpleTable key={`${objectType}-relationships-table`} {...tableProps} />
      {previewContentDialogProps.open && (
        <PreviewContentDialog {...previewContentDialogProps} />
      )}
    </>
  );
};

const searchItemsByProgramQuery = `
  query SearchItemsByProgram($name: String, $program: ID, $rowLimit: Int) {
    itemsByMetadata(name: $name, program: $program, rowLimit: $rowLimit) {
      id
      name
    }
  }`;

const searchPassagesByProgramQuery = `
  query searchPassagesByMetadata($name: String, $program: ID, $rowLimit: Int) {
    passagesByMetadata(name: $name, program: $program, rowLimit: $rowLimit) {
      id
      name
    }
  }`;

export {
  RelationshipsTable,
  searchItemsByProgramQuery,
  searchPassagesByProgramQuery,
};
